import React, { useMemo } from 'react';
import * as BS from 'react-bootstrap';
import * as BSEx from '../../../components/BootstrapExtends/index.js';
import Box from '../../atoms/Box';
import Column from '../../../models/SpreadsheetReportConfigs/Column';
import PropTypes from 'prop-types';
import Sort from '../../../models/SpreadsheetReportConfigs/Sort';
import {
  DEFAULT_SORT_FIELD,
  OTHER_COLUMN_VALUES,
  UNSELECTABLE_SORT_COLUMNS,
} from '../../../constants/SpreadsheetReportConfigs';
import {
  SortDirectionOptions,
  SelectableSortColumns,
} from '../../../enums/SpreadsheetReportConfigs';

const scope =
  'frontend.components.SpreadsheetReportConfigs.Form.CustomReportForm.sheetConfigSection.panelSection.outputDataSorterSection';

const OutputDataSorter = ({
  selectedColumns,
  sorts,
  errors,
  touched,
  onChange,
  onBlur,
}) => {
  const getColumnLabel = (currentColumn) => {
    const columnEnum = Object.values(SelectableSortColumns).find(
      (column) => column.value === currentColumn.content
    );
    return columnEnum ? columnEnum.label : currentColumn.name;
  };

  // Create options for a specific sort field index
  const getSortFieldOptions = (currentIndex) => {
    return selectedColumns
      .filter(
        (column) =>
          ![OTHER_COLUMN_VALUES.blank, ...UNSELECTABLE_SORT_COLUMNS].includes(
            column.content
          ) &&
          // Include current sort's option or options not selected in other sorts
          (sorts[currentIndex]?.targetColumn === column.content ||
            !sorts.some(
              (sort, sortIndex) =>
                sortIndex !== currentIndex &&
                sort.targetColumn === column.content
            ))
      )
      .map((column) => ({
        value: column.content,
        label: getColumnLabel(column),
      }));
  };

  const hasAvailableColumns = useMemo(() => {
    const validColumns = selectedColumns.filter(
      (column) => column.content !== OTHER_COLUMN_VALUES.blank
    );
    return sorts.length < validColumns.length;
  }, [selectedColumns, sorts]);

  const getError = (index, field) => {
    return errors.sorts?.[index]?.[field];
  };

  const getTouched = (index, field) => {
    return touched.sorts?.[index]?.[field];
  };

  const handleSortChange = (index, field) => (event) => {
    const newSorts = [...sorts];
    if (field === 'targetColumn') {
      newSorts[index] = {
        ...newSorts[index],
        targetColumn: event.target.value,
      };
    } else if (field === 'isAsc') {
      newSorts[index] = {
        ...newSorts[index],
        isAsc: event.target.value === 'true',
      };
    }
    onChange(newSorts);
  };

  const handleAddSortField = () => {
    onChange([...sorts, DEFAULT_SORT_FIELD]);
  };

  const handleBlur = (index, field) => {
    onBlur(`sorts[${index}].${field}`);
  };

  const handleRemoveSortField = (index) => {
    const newSorts = sorts.filter((_, i) => i !== index);
    onChange(newSorts);
  };

  return (
    <>
      <h4>{I18n.t(`${scope}.sectionTitle`)}</h4>
      <Box>
        <Box.Body>
          {sorts.map((sort, index) => (
            <BS.Row key={index}>
              <BS.Col sm={4}>
                <BSEx.FormGroupInline
                  error={getError(index, 'targetColumn')}
                  touched={getTouched(index, 'targetColumn')}
                >
                  {/* Sort Field */}
                  <BS.FormControl
                    componentClass='select'
                    value={sort.targetColumn}
                    onChange={handleSortChange(index, 'targetColumn')}
                    onBlur={() => handleBlur(index, 'targetColumn')}
                  >
                    <option value='' style={{ display: 'none' }}>
                      {I18n.t(`${scope}.field.sortField.placeholder`)}
                    </option>
                    {getSortFieldOptions(index).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </BS.FormControl>
                </BSEx.FormGroupInline>
              </BS.Col>

              <BS.Col sm={2}>
                {/* Sort Direction */}
                {Object.entries(SortDirectionOptions).map(([key, option]) => (
                  <BS.Radio
                    key={key}
                    name={`sort-direction-${index}`}
                    value={option.value}
                    checked={sort.isAsc === option.value}
                    onChange={handleSortChange(index, 'isAsc')}
                    inline
                  >
                    {option.label}
                  </BS.Radio>
                ))}
              </BS.Col>

              <BS.Col
                sm={1}
                className='control-label'
                style={{ textAlign: 'left' }}
              >
                <BS.Button
                  bsStyle='danger'
                  bsSize='xs'
                  type='button'
                  onClick={() => handleRemoveSortField(index)}
                >
                  <i className='fa fa-minus' />
                </BS.Button>
              </BS.Col>
            </BS.Row>
          ))}

          <BS.Button
            bsStyle='default'
            type='button'
            onClick={handleAddSortField}
            disabled={!hasAvailableColumns}
          >
            {I18n.t(`${scope}.action.addSortField`)}
          </BS.Button>
        </Box.Body>
      </Box>
    </>
  );
};

OutputDataSorter.propTypes = {
  selectedColumns: PropTypes.arrayOf(PropTypes.shape(Column.propTypes)),
  sorts: PropTypes.arrayOf(PropTypes.shape(Sort.propTypes)),
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
};

export default OutputDataSorter;
